import React, { useState, useEffect, useContext } from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import { getContrastColor } from '../../utils/colorUtils';
import {
  normalizeHex, Container, Title, SettingsGrid, SettingCard, SettingTitle,
  ColorGrid, ColorOption, ColorLabel, ThemeOption, PreviewBox, ButtonGroup, Button,
} from './Settings.styles';

function Settings() {
  const { theme, updateTheme, resetTheme } = useContext(ThemeContext);
  const [localTheme, setLocalTheme] = useState(theme);

  useEffect(() => {
    setLocalTheme(theme);
  }, [theme]);

  const themes = [
    { name: 'Dark (Default)', navbar: '#000000', background: '#000000', text: '#ffffff', accent: '#667eea', modalBg: '#1a1a1a', cardTitle: '#1a1a2e' },
    { name: 'Dark Blue',      navbar: '#0a1628', background: '#0f1419', text: '#e0e0e0', accent: '#00a8ff', modalBg: '#0f1419', cardTitle: '#0a1628' },
    { name: 'Dark Green',     navbar: '#0d1b0f', background: '#000a02', text: '#ffffff', accent: '#00d084', modalBg: '#0d1b0f', cardTitle: '#0d1b0f' },
    { name: 'Dark Purple',    navbar: '#1a0a2e', background: '#0f0015', text: '#f5f5f5', accent: '#c200ff', modalBg: '#1a0a2e', cardTitle: '#1a0a2e' },
    { name: 'Dark Red',       navbar: '#1a0000', background: '#0a0000', text: '#ffffff', accent: '#ff4444', modalBg: '#1a0000', cardTitle: '#1a0000' },
    { name: 'Custom', navbar: localTheme.navbar, background: localTheme.background, text: localTheme.text, accent: localTheme.accent, modalBg: localTheme.modalBg, cardTitle: localTheme.cardTitle },
  ];

  const handleThemeSelect = (selectedTheme) => {
    setLocalTheme(selectedTheme);
    updateTheme(selectedTheme);
  };

  const handleColorChange = (colorType, color) => {
    const newTheme = { ...localTheme, [colorType]: color };
    setLocalTheme(newTheme);
    updateTheme(newTheme);
  };

  const handleSave = () => {
    alert('✓ Tema guardado exitosamente!');
  };

  const handleResetTheme = () => {
    resetTheme();
    setLocalTheme({ navbar: '#000000', background: '#000000', text: '#ffffff', accent: '#667eea', modalBg: '#1a1a1a', cardTitle: '#1a1a2e' });
  };

  const colors = [
    { name: 'Negro',      hex: '#000000' },
    { name: 'Gris Oscuro',hex: '#1a1a1a' },
    { name: 'Gris',       hex: '#444444' },
    { name: 'Blanco',     hex: '#ffffff' },
    { name: 'Azul',       hex: '#00a8ff' },
    { name: 'Verde',      hex: '#00d084' },
    { name: 'Púrpura',    hex: '#c200ff' },
    { name: 'Rojo',       hex: '#ff4444' },
    { name: 'Naranja',    hex: '#ff8800' },
  ];

  return (
    <Container bgColor={localTheme.background}>
      <Title bgColor={localTheme.background}>⚙️ Configuración de Tema</Title>

      <SettingsGrid>
        {/* TEMAS PREDEFINIDOS */}
        <SettingCard bgColor={localTheme.modalBg} borderColor={localTheme.accent}>
          <SettingTitle bgColor={localTheme.modalBg}>Temas Predefinidos</SettingTitle>
          {themes.slice(0, 5).map((t, idx) => (
            <ThemeOption key={idx} background={t.navbar} color={t.text}
              selected={localTheme.navbar === t.navbar && localTheme.accent === t.accent}
              onClick={() => handleThemeSelect(t)}>
              {t.name}
            </ThemeOption>
          ))}
        </SettingCard>

        {/* COLOR DEL NAVBAR */}
        <SettingCard bgColor={localTheme.modalBg} borderColor={localTheme.accent}>
          <SettingTitle bgColor={localTheme.modalBg}>Color Barra Superior</SettingTitle>
          <ColorGrid>
            {colors.map((color) => (
              <ColorOption key={color.hex} color={color.hex}
                selected={normalizeHex(localTheme.navbar) === normalizeHex(color.hex)}
                onClick={() => handleColorChange('navbar', color.hex)}>
                <ColorLabel textColor={getContrastColor(color.hex)}>{color.name}</ColorLabel>
              </ColorOption>
            ))}
          </ColorGrid>
        </SettingCard>

        {/* COLOR DEL FONDO */}
        <SettingCard bgColor={localTheme.modalBg} borderColor={localTheme.accent}>
          <SettingTitle bgColor={localTheme.modalBg}>Color Fondo Principal</SettingTitle>
          <ColorGrid>
            {colors.map((color) => (
              <ColorOption key={color.hex} color={color.hex}
                selected={normalizeHex(localTheme.background) === normalizeHex(color.hex)}
                onClick={() => handleColorChange('background', color.hex)}>
                <ColorLabel textColor={getContrastColor(color.hex)}>{color.name}</ColorLabel>
              </ColorOption>
            ))}
          </ColorGrid>
        </SettingCard>

        {/* COLOR DE TEXTO */}
        <SettingCard bgColor={localTheme.modalBg} borderColor={localTheme.accent}>
          <SettingTitle bgColor={localTheme.modalBg}>Color Texto</SettingTitle>
          <ColorGrid>
            {colors.map((color) => (
              <ColorOption key={color.hex} color={color.hex}
                selected={normalizeHex(localTheme.text) === normalizeHex(color.hex)}
                onClick={() => handleColorChange('text', color.hex)}>
                <ColorLabel textColor={getContrastColor(color.hex)}>{color.name}</ColorLabel>
              </ColorOption>
            ))}
          </ColorGrid>
        </SettingCard>

        {/* COLOR ACENTO */}
        <SettingCard bgColor={localTheme.modalBg} borderColor={localTheme.accent}>
          <SettingTitle bgColor={localTheme.modalBg}>Color Acentos y Botones</SettingTitle>
          <ColorGrid>
            {[
              { name: 'Púrpura', hex: '#667eea' }, { name: 'Azul',    hex: '#00a8ff' },
              { name: 'Verde',   hex: '#00d084' },  { name: 'Rojo',    hex: '#ff4444' },
              { name: 'Naranja', hex: '#ff8800' },  { name: 'Rosado',  hex: '#ff6b9d' },
              { name: 'Cyan',    hex: '#00e5ff' },  { name: 'Amarillo',hex: '#ffdd00' },
              { name: 'Lima',    hex: '#76ff03' },
            ].map((color) => (
              <ColorOption key={color.hex} color={color.hex}
                selected={normalizeHex(localTheme.accent) === normalizeHex(color.hex)}
                onClick={() => handleColorChange('accent', color.hex)}>
                <ColorLabel textColor={getContrastColor(color.hex)}>{color.name}</ColorLabel>
              </ColorOption>
            ))}
          </ColorGrid>
        </SettingCard>

        {/* COLOR FONDO MODAL */}
        <SettingCard bgColor={localTheme.modalBg} borderColor={localTheme.accent}>
          <SettingTitle bgColor={localTheme.modalBg}>Color Fondo Modal</SettingTitle>
          <ColorGrid>
            {[
              { name: 'Blanco',     hex: '#ffffff' }, { name: 'Gris Claro', hex: '#f5f5f5' },
              { name: 'Gris',       hex: '#e8e8e8' }, { name: 'Gris Oscuro',hex: '#1a1a1a' },
              { name: 'Negro',      hex: '#0a0a0a' }, { name: 'Azul Claro', hex: '#e3f2fd' },
            ].map((color) => (
              <ColorOption key={color.hex} color={color.hex}
                selected={normalizeHex(localTheme.modalBg) === normalizeHex(color.hex)}
                onClick={() => handleColorChange('modalBg', color.hex)}>
                <ColorLabel textColor={getContrastColor(color.hex)}>{color.name}</ColorLabel>
              </ColorOption>
            ))}
          </ColorGrid>
        </SettingCard>

        {/* COLOR TÍTULO TARJETA */}
        <SettingCard bgColor={localTheme.modalBg} borderColor={localTheme.accent}>
          <SettingTitle bgColor={localTheme.modalBg}>Color Título de Tarjeta</SettingTitle>
          <ColorGrid>
            {[
              { name: 'Azul Noche',  hex: '#1a1a2e' }, { name: 'Azul Marino',hex: '#0a1628' },
              { name: 'Púrpura',     hex: '#1a0a2e' }, { name: 'Verde Oscuro',hex: '#0d1b0f' },
              { name: 'Rojo Oscuro', hex: '#1a0000' }, { name: 'Marrón',      hex: '#2a1a00' },
            ].map((color) => (
              <ColorOption key={color.hex} color={color.hex}
                selected={normalizeHex(localTheme.cardTitle) === normalizeHex(color.hex)}
                onClick={() => handleColorChange('cardTitle', color.hex)}>
                <ColorLabel textColor={getContrastColor(color.hex)}>{color.name}</ColorLabel>
              </ColorOption>
            ))}
          </ColorGrid>
        </SettingCard>

        {/* VISTA PREVIA */}
        <SettingCard bgColor={localTheme.modalBg} borderColor={localTheme.accent}>
          <SettingTitle bgColor={localTheme.modalBg}>Vista Previa</SettingTitle>
          <PreviewBox bgColor={localTheme.navbar} textColor={localTheme.text} borderColor={localTheme.accent}>
            <strong>Barra de Navegación</strong>
          </PreviewBox>
          <PreviewBox bgColor={localTheme.background} textColor={localTheme.text} borderColor={localTheme.accent} style={{ marginTop: '10px' }}>
            Contenido Principal
          </PreviewBox>
          <PreviewBox bgColor={localTheme.cardTitle || '#1a1a2e'} textColor={localTheme.accent} borderColor={localTheme.accent} style={{ marginTop: '10px' }}>
            Título de Tarjeta
          </PreviewBox>
          <PreviewBox bgColor={localTheme.accent} textColor={'#fff'} borderColor={localTheme.accent} style={{ marginTop: '10px' }}>
            Botón Acento
          </PreviewBox>
        </SettingCard>
      </SettingsGrid>

      <ButtonGroup>
        <Button onClick={handleResetTheme}>Restaurar Predeterminados</Button>
        <Button primary onClick={handleSave}>Guardar Configuración</Button>
      </ButtonGroup>
    </Container>
  );
}

export default Settings;
